import type { AssistantCard, AssistantCardKind, AssistantToolCallView } from '@fieldops/shared'
import { useState } from 'react'
import { cn } from '../../../lib/cn.js'
import { money } from '../../../lib/format.js'
import { Icon, type IconName } from '../../Icon.js'
import { Badge, Button, Spinner } from '../../ui.js'

/**
 * One thing the assistant did or wants to do, in the conversation.
 *
 * The tier decides the shape. A read is a quiet step line. A note, task,
 * person or brief was written already, so it is a receipt with Undo. A
 * booking, a price, a stage or anything going to a customer is a card that
 * waits: nothing happens until Approve, and what it shows is drawn from the
 * same arguments the server will run. Decision 94.
 */

const KIND_ICON: Record<AssistantCardKind, IconName> = {
  email: 'mail',
  document: 'send',
  booking: 'schedule',
  lines: 'pricing',
  stage: 'refresh',
  proposal: 'cover',
  invoice: 'cash',
  sms: 'phone',
  note: 'note',
  task: 'task',
  contact: 'people',
  service: 'spray',
  brief: 'text',
  job: 'jobs',
  client: 'clients',
  agreement: 'agreement',
  report: 'flag',
}

const APPROVE_LABEL: Partial<Record<AssistantCardKind, string>> = {
  email: 'Approve & send',
  document: 'Approve & send',
  booking: 'Approve & book',
  invoice: 'Approve & issue',
  sms: 'Open in SMS',
  report: 'Send to the backlog',
}

export interface ActionCardProps {
  call: AssistantToolCallView
  /** A run is going; the buttons wait for it. */
  busy: boolean
  /** With `edits`, the card's editable fields as the person changed them (decision 116). */
  onApprove: (edits?: Record<string, unknown>) => void
  onReject: (reason?: string, resume?: boolean) => void
  onEditInComposer?: () => void
  onUndo: () => void
  undoing?: boolean
}

export function ActionCard(props: ActionCardProps) {
  const { call } = props
  // A proposal the model got wrong before there was anything to show (bad
  // arguments, a refusal) is a quiet line: it tries again, and a red card for
  // a slip it has already fixed reads as a failure the person must deal with.
  if (call.tier === 'read' || (call.status === 'FAILED' && !call.card)) return <Step call={call} />
  if (call.tier === 'immediate') return <Receipt {...props} />
  return <Proposal {...props} />
}

function Step({ call }: { call: AssistantToolCallView }) {
  const failed = call.status === 'FAILED'
  // A step that did not work is the model's to deal with — it reads the
  // reason and tries again or says so — so it is grey, not the problem red.
  return (
    <p className={cn('flex items-start gap-1.5 text-[12px]', failed ? 'text-ink-400' : 'text-ink-500')}>
      <Icon name={failed ? 'alert' : 'check'} className="mt-px size-3.5 shrink-0" />
      <span>
        {call.label}
        {failed && call.error ? ` — ${call.error}` : null}
      </span>
    </p>
  )
}

function Receipt({ call, onUndo, undoing, busy }: ActionCardProps) {
  const card = call.card
  const failed = call.status === 'FAILED'
  return (
    <div
      className={cn(
        'flex items-start gap-2.5 rounded-lg border bg-white px-3 py-2.5',
        failed ? 'border-red-200' : 'border-ink-200',
      )}
    >
      <span
        className={cn(
          'mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-md',
          failed ? 'bg-red-50 text-red-700' : 'bg-brand-100 text-brand-700',
        )}
      >
        <Icon name={failed ? 'alert' : card ? KIND_ICON[card.kind] : 'check'} className="size-3.5" />
      </span>
      <div className="min-w-0 flex-1">
        <p className={cn('text-[13px] font-medium', call.undone ? 'text-ink-400 line-through' : 'text-ink-900')}>
          {failed ? `Couldn’t: ${call.label.toLowerCase()}` : call.label}
        </p>
        {failed && call.error ? <p className="text-[12px] text-red-700">{call.error}</p> : null}
        {!failed && card ? <ReceiptDetail card={card} /> : null}
      </div>
      {call.undone ? (
        <span className="text-[12px] text-ink-400">Undone</span>
      ) : call.undoable ? (
        <Button
          size="sm"
          variant="ghost"
          icon={<Icon name="undo" className="size-3.5" />}
          loading={undoing}
          disabled={busy}
          onClick={onUndo}
        >
          Undo
        </Button>
      ) : null}
    </div>
  )
}

function ReceiptDetail({ card }: { card: AssistantCard }) {
  const detail = card.body ?? card.fields?.map((field) => `${field.label}: ${field.value}`).join(' · ') ?? null
  return (
    <>
      {card.kind === 'note' || card.kind === 'brief' ? null : (
        <p className="truncate text-[12.5px] text-ink-600">{card.title}</p>
      )}
      {detail ? <p className="line-clamp-3 text-[12.5px] whitespace-pre-wrap text-ink-600">{detail}</p> : null}
    </>
  )
}

function statusBadge(call: AssistantToolCallView) {
  switch (call.status) {
    case 'AWAITING_APPROVAL':
      return (
        <Badge tone="dark">
          Needs approval
        </Badge>
      )
    case 'PROPOSED':
      return (
        <Badge tone="neutral" className="gap-1">
          <Spinner className="size-3" /> Working
        </Badge>
      )
    case 'EXECUTED':
      return (
        <Badge tone="green">
          {call.card?.kind === 'sms' && call.card.sms?.via !== 'server' ? 'Opened' : 'Done'}
        </Badge>
      )
    case 'REJECTED':
      return (
        <Badge tone="neutral">
          Not done
        </Badge>
      )
    case 'FAILED':
      return (
        <Badge tone="red">
          Didn’t go
        </Badge>
      )
  }
}

function Proposal({ call, busy, onApprove, onReject, onEditInComposer }: ActionCardProps) {
  const card: AssistantCard = call.card ?? { kind: 'note', title: call.label }
  const waiting = call.status === 'AWAITING_APPROVAL'
  const draft = card.kind === 'email' || card.kind === 'sms'
  const [editing, setEditing] = useState<Record<string, unknown> | null>(null)
  const canChange = waiting && call.editable.length > 0 && call.draft !== null

  return (
    <article
      aria-label={card.title}
      className={cn(
        'overflow-hidden rounded-lg border border-ink-200 bg-white',
        waiting
          ? 'shadow-[0_1px_2px_rgb(16_24_40/0.05),0_6px_16px_-4px_rgb(16_24_40/0.10)]'
          : 'shadow-[0_1px_2px_rgb(16_24_40/0.04)]',
      )}
    >
      <header className="flex items-center gap-2 px-4 pt-3 pb-1">
        <Icon name={KIND_ICON[card.kind]} className="size-4 shrink-0 text-ink-400" />
        <p className="min-w-0 flex-1 truncate text-[13px]">
          <span className="font-semibold text-ink-900">{card.title}</span>
          {card.subtitle ? <span className="text-ink-500"> {card.subtitle}</span> : null}
        </p>
        {statusBadge(call)}
      </header>
      {card.job ? (
        <p className="px-4 text-[12px] text-ink-500">
          Job <span className="font-medium text-ink-700">{card.job.number}</span> · {card.job.client}
        </p>
      ) : null}

      {editing ? (
        <DraftForm
          values={editing}
          keys={call.editable}
          onChange={setEditing}
        />
      ) : null}

      <div className={cn('flex flex-col gap-3 px-4 pt-2 pb-3.5', editing && 'hidden')}>
        {card.body ? (
          <div className="flex flex-col gap-2 text-[13.5px] leading-relaxed text-ink-800">
            {card.body.split(/\n\s*\n/).map((paragraph, index) => (
              <p key={index} className="whitespace-pre-wrap">
                {paragraph.trim()}
              </p>
            ))}
          </div>
        ) : null}

        {card.fields?.length ? (
          <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-[13px]">
            {card.fields.map((field) => (
              <div key={field.label} className="contents">
                <dt className="text-ink-500">{field.label}</dt>
                <dd className="text-ink-900">{field.value}</dd>
              </div>
            ))}
          </dl>
        ) : null}

        {card.lines?.length ? (
          <table className="w-full text-[13px]">
            <thead>
              <tr className="text-left text-[11px] tracking-wide text-ink-500 uppercase">
                <th className="pb-1 font-medium">Item</th>
                <th className="pb-1 text-right font-medium">Qty</th>
                <th className="pb-1 text-right font-medium">Each</th>
                <th className="pb-1 text-right font-medium">Total</th>
              </tr>
            </thead>
            <tbody>
              {card.lines.map((line, index) => (
                <tr key={index} className="border-t border-ink-100">
                  <td className="py-1 pr-2 text-ink-900">{line.description}</td>
                  <td className="py-1 text-right tabular-nums text-ink-700">{line.quantity}</td>
                  <td className="py-1 text-right tabular-nums text-ink-700">{money(line.unitPriceIncTax)}</td>
                  <td className="py-1 text-right tabular-nums text-ink-900">{money(line.totalIncTax)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}

        {card.totalIncTax != null ? (
          <p className="text-right text-[13.5px] font-semibold text-ink-900">
            {money(card.totalIncTax)} <span className="font-normal text-ink-500">inc GST</span>
          </p>
        ) : null}

        {card.attachments?.length ? (
          <ul className="flex flex-wrap gap-1.5" aria-label="Attached">
            {card.attachments.map((file) => (
              <li
                key={file.id}
                className="inline-flex items-center gap-1 rounded-md bg-ink-50 px-2 py-0.5 text-[12px] text-ink-700 ring-1 ring-ink-200 ring-inset"
              >
                <Icon name="paperclip" className="size-3" />
                {file.name}
              </li>
            ))}
          </ul>
        ) : null}

        {card.sources?.length ? (
          <div className="flex flex-wrap items-center gap-1.5 text-[11.5px]">
            <span className="text-ink-400">Used</span>
            {card.sources.map((source) => (
              <span key={source} className="rounded-md px-1.5 py-px text-ink-600 ring-1 ring-ink-200 ring-inset">
                {source}
              </span>
            ))}
          </div>
        ) : null}

        {card.warning ? (
          <p className="flex items-start gap-1.5 rounded-md border-l-[3px] border-ink-900 bg-ink-50 px-2.5 py-1.5 text-[12.5px] text-ink-800">
            <Icon name="alert" className="mt-px size-3.5 shrink-0" />
            {card.warning}
          </p>
        ) : null}

        {call.status === 'FAILED' && call.error ? (
          <p className="rounded-md bg-red-50 px-2.5 py-1.5 text-[12.5px] text-red-700">{call.error}</p>
        ) : null}
        {call.status === 'REJECTED' && call.rejectedReason ? (
          <p className="text-[12px] text-ink-500">{call.rejectedReason}</p>
        ) : null}
        {call.status === 'EXECUTED' && call.approvedByName ? (
          <p className="text-[12px] text-ink-500">Approved by {call.approvedByName}</p>
        ) : null}
      </div>

      {waiting ? (
        <footer className="flex flex-wrap items-center gap-2 border-t border-ink-100 bg-ink-50 px-4 py-2.5">
          <Button
            size="sm"
            variant="primary"
            icon={<Icon name="check" className="size-3.5" />}
            disabled={busy}
            onClick={() => onApprove(editing ?? undefined)}
          >
            {card.sms?.via === 'server' ? 'Approve & send' : (APPROVE_LABEL[card.kind] ?? 'Approve')}
          </Button>
          {canChange ? (
            <Button size="sm" disabled={busy} onClick={() => setEditing(editing ? null : { ...call.draft })}>
              {editing ? 'Keep as drafted' : 'Change'}
            </Button>
          ) : null}
          {card.kind === 'email' && onEditInComposer ? (
            <Button size="sm" disabled={busy} onClick={onEditInComposer}>
              Edit in composer
            </Button>
          ) : null}
          <Button size="sm" variant="ghost" disabled={busy} onClick={() => onReject()}>
            Don’t
          </Button>
          {draft ? (
            <div className="ml-auto flex items-center gap-3 text-[12.5px] font-medium">
              <button
                type="button"
                className="text-brand-700 hover:underline disabled:opacity-50"
                disabled={busy}
                onClick={() => onReject('Make it shorter.', true)}
              >
                Shorter
              </button>
              {card.kind === 'email' ? (
                <button
                  type="button"
                  className="text-brand-700 hover:underline disabled:opacity-50"
                  disabled={busy}
                  onClick={() => onReject('Write this as an SMS instead, with draft_sms.', true)}
                >
                  As SMS
                </button>
              ) : null}
            </div>
          ) : null}
        </footer>
      ) : null}
    </article>
  )
}

/* -------------------------------------------------------------------------- */
/* Changing a card before Approve                                             */
/* -------------------------------------------------------------------------- */

const FIELD_LABEL: Record<string, string> = {
  kind: 'Kind',
  summary: 'In a line',
  details: 'What happened, or what you need',
  to: 'To',
  cc: 'Cc',
  subject: 'Subject',
  body: 'Message',
  date: 'Date',
  startTime: 'Start',
  durationMinutes: 'Minutes',
  notes: 'Notes',
  description: 'What the job is',
  dueOn: 'Day',
  reason: 'Why',
  endsOn: 'Term ends',
  autoRenew: 'Renews by itself',
  status: 'Status',
  name: 'Client name',
  firstName: 'First name',
  lastName: 'Last name',
  mobile: 'Mobile',
  email: 'Email',
}

const LONG = new Set(['body', 'notes', 'description', 'reason', 'details'])

/**
 * The card's editable arguments as a small form. What is typed goes back to
 * the server with Approve, which checks it with the tool's own rules and
 * draws the card again before anything runs (decision 116).
 */
function DraftForm({
  values,
  keys,
  onChange,
}: {
  values: Record<string, unknown>
  keys: string[]
  onChange: (values: Record<string, unknown>) => void
}) {
  const set = (key: string, value: unknown) => onChange({ ...values, [key]: value })
  return (
    <div className="flex flex-col gap-2.5 px-4 pt-2 pb-3.5" role="group" aria-label="Change before approving">
      {keys.map((key) => {
        const value = values[key]
        const label = FIELD_LABEL[key] ?? key
        const id = `draft-${key}`
        const input = 'w-full rounded-md bg-white px-2.5 py-1.5 text-[13px] text-ink-900 ring-1 ring-ink-300 ring-inset outline-none focus:ring-2 focus:ring-brand-600'
        if (key === 'kind' && (value === 'bug' || value === 'feature')) {
          return (
            <div key={key} className="flex flex-col gap-1">
              <span className="text-[11.5px] font-medium text-ink-600">{label}</span>
              <div className="flex gap-1" role="radiogroup" aria-label={label}>
                {(['feature', 'bug'] as const).map((option) => (
                  <button
                    key={option}
                    type="button"
                    role="radio"
                    aria-checked={value === option}
                    onClick={() => set(key, option)}
                    className={cn(
                      'rounded-md px-2.5 py-1 text-[12.5px] font-medium ring-1 ring-inset',
                      value === option ? 'bg-ink-900 text-white ring-ink-900' : 'bg-white text-ink-700 ring-ink-300 hover:bg-ink-50',
                    )}
                  >
                    {option === 'bug' ? 'Bug' : 'Feature request'}
                  </button>
                ))}
              </div>
            </div>
          )
        }
        if (typeof value === 'boolean' || key === 'autoRenew') {
          return (
            <label key={key} className="flex items-center gap-2 text-[13px] text-ink-800">
              <input type="checkbox" checked={Boolean(value)} onChange={(event) => set(key, event.target.checked)} />
              {label}
            </label>
          )
        }
        return (
          <div key={key} className="flex flex-col gap-1">
            <label htmlFor={id} className="text-[11.5px] font-medium text-ink-600">
              {label}
            </label>
            {LONG.has(key) ? (
              <textarea
                id={id}
                rows={key === 'body' ? 6 : 3}
                value={String(value ?? '')}
                onChange={(event) => set(key, event.target.value)}
                className={cn(input, 'resize-y leading-relaxed')}
              />
            ) : Array.isArray(value) || key === 'to' || key === 'cc' ? (
              <input
                id={id}
                value={Array.isArray(value) ? value.join(', ') : String(value ?? '')}
                onChange={(event) =>
                  set(
                    key,
                    event.target.value
                      .split(/[,;\s]+/)
                      .map((part) => part.trim())
                      .filter(Boolean),
                  )
                }
                className={input}
              />
            ) : (
              <input
                id={id}
                type={key === 'date' || key === 'dueOn' || key === 'endsOn' ? 'date' : key === 'startTime' ? 'time' : typeof value === 'number' || key === 'durationMinutes' ? 'number' : 'text'}
                value={value === null || value === undefined ? '' : String(value)}
                onChange={(event) =>
                  set(key, typeof value === 'number' || key === 'durationMinutes' ? Number(event.target.value) : event.target.value || null)
                }
                className={input}
              />
            )}
          </div>
        )
      })}
    </div>
  )
}
